import React, { useState, useRef, useEffect } from 'react';
import { FileSliders, Globe, ChevronDown } from 'lucide-react';
import { DataSourceType } from 'pages/Knowledge/types';

interface PreviewPanelProps {
  selectedDataSource: string;
  crawlResults: any | null;
  selectedPages: Set<number>;
  isLoadingPreview: boolean;
  selectedPreviewDocument: number;
  onPreviewDocumentChange: (index: number) => void;
  onFetchPreview: () => void;
}

const PreviewPanel: React.FC<PreviewPanelProps> = ({
  selectedDataSource,
  crawlResults,
  selectedPages,
  isLoadingPreview,
  selectedPreviewDocument,
  onPreviewDocumentChange,
  onFetchPreview
}) => {
  // Dropdown state for chunk list - hooks must be called before any early returns
  const [openList, setOpenList] = useState(false);
  const dropdownRef = useRef<HTMLDivElement | null>(null);
  const buttonRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    if (!openList) return;
    const handler = (e: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(e.target as Node)
      ) {
        setOpenList(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [openList]);

  const documents = (selectedDataSource === DataSourceType.WebScraping ? crawlResults?.data.filter((_: any, index: any) => selectedPages.has(index)) || [] : crawlResults?.data || []);
  const selectedDocument = documents.length > 0
    ? documents[selectedPreviewDocument] || documents[0]
    : null;

  // Get chunks from the selected document's segments
  const chunks = (selectedDocument?.segments || [])
  const chunkCount = chunks.length;
  const documentCount = documents.length || 0;
  const title = selectedDocument
    ? selectedDocument.title
    : 'Preview';

  const getDocumentTitle = (doc: any, index: number) => {
    return doc?.title || `Document ${index + 1}`;
  };

  const handleSelectChunk = (index: number) => {
    onPreviewDocumentChange(index);
    setOpenList(false);
    // Scroll to chunk body
    requestAnimationFrame(() => {
      const el = document.getElementById(`chunk-${index}`);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    });
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4 flex flex-col h-full relative">
      {/* Header */}
      <div className="flex items-start justify-between mb-4 relative">
        <div className="flex flex-col relative">
          <div className="flex items-center gap-1 text-[10px] font-medium text-blue-600 tracking-wide mb-1">PREVIEW</div>
          <button
            ref={buttonRef}
            type="button"
            onClick={() => setOpenList(o => !o)}
            className="group inline-flex items-center text-sm font-semibold text-gray-900 hover:text-blue-600 transition-colors focus:outline-none"
            aria-haspopup="listbox"
            aria-expanded={openList}
          >
            {selectedDataSource === DataSourceType.WebScraping ? <Globe className="w-4 h-4 mr-1 text-blue-500" /> : <FileSliders className="w-4 h-4 mr-1 text-blue-500" />}
            <span className="truncate max-w-[220px] text-left">{title}</span>
            <ChevronDown className={`w-4 h-4 ml-1 transition-transform text-gray-400 group-hover:text-blue-500 ${openList ? 'rotate-180' : ''}`} />
          </button>
          {openList && (selectedDataSource === DataSourceType.WebScraping ? documentCount > 0 : chunkCount > 0) && (
            <div
              ref={dropdownRef}
              className="absolute top-full mt-2 w-72 max-h-72 overflow-auto rounded-md border border-gray-200 bg-white shadow-lg z-20 text-sm" role="listbox"
            >
              <div className="sticky top-0 bg-white px-3 py-2 border-b border-gray-100 flex items-center justify-between">
                <span className="font-medium text-gray-500">
                  {documentCount} Documents
                </span>
              </div>
              <ul className="divide-y divide-gray-100">
                {documents.map((doc: any, i: number) => (
                  <li
                    key={i}
                    className={`px-3 py-2 cursor-pointer hover:bg-blue-50 group ${selectedPreviewDocument === i ? 'bg-blue-50/70' : ''}`}
                    onClick={() => handleSelectChunk(i)}
                    role="option"
                    aria-selected={selectedPreviewDocument === i}
                  >
                    <div className="flex items-start gap-2">
                      <div className="flex-1 min-w-0">
                        <div className="text-gray-700 group-hover:text-blue-700 line-clamp-1 font-medium">
                          {getDocumentTitle(doc, i)}
                        </div>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
        {chunkCount > 0 && (
          <span className="ml-2 inline-flex items-center rounded-full bg-blue-50 px-2 py-1 text-[10px] font-medium text-gray-600 uppercase tracking-wide">
            {chunkCount} Estimated Chunks
          </span>
        )}
      </div>

      {/* Body */}
      {isLoadingPreview ? (
        <div className="flex-1 flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-6 w-6 border-2 border-blue-200 border-t-blue-600"></div>
        </div>
      ) : chunkCount > 0 ? (
        <div className="space-y-4 overflow-y-auto pr-2 custom-scrollbar">
          {chunks.map((chunk: any, index: number) => {
            const content = chunk?.content || '';
            const length = content.length;
            return (
              <div key={index} id={`chunk-${index}`} className="group scroll-mt-4">
                <div className="flex items-center text-[11px] font-medium text-gray-600 mb-1">
                  <span className="text-gray-900 font-semibold mr-1"># Chunk-{index + 1}</span>
                  <span className="text-gray-400">· {length} characters</span>
                </div>
                <div className="rounded-md bg-white px-3 py-2 leading-relaxed shadow-sm transition-colors">
                  <p className="text-sm text-gray-800 whitespace-pre-wrap">{content}</p>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="flex-1 flex flex-col items-center justify-center text-center border-2 border-dashed border-gray-300 rounded-lg p-6">
          <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center mb-3">
            <span className="text-gray-400 text-lg">👁</span>
          </div>
          <p className="text-sm text-gray-600 max-w-xs">Click the 'Preview Chunk' button on the left to load the preview</p>
        </div>
      )}
    </div>
  );
};

export default PreviewPanel;
